import React from 'react';
import { Navigate } from 'react-router-dom';
import { PermissionRoute } from '../components/PermissionRoute';
import { TAB_KEEP_ALIVE_EXCLUDED_PATHS } from '../config/tabKeepAlive';
import { setProtectedRouteDefinitions } from './resolveRouteEntry';
import type { ProtectedRouteComponents, RouteDefinition, RouteComponent } from './types';

const withPermissionElement = (
  permission: string,
  element: React.ReactNode,
) => (
  <PermissionRoute requiredPermission={permission}>
    {element}
  </PermissionRoute>
);

const withPermission = (
  permission: string,
  Page: RouteComponent,
) => withPermissionElement(permission, <Page />);

const page = (Page: RouteComponent) => <Page />;

const route = (
  path: string,
  element: React.ReactNode,
  options?: { keepAlive?: boolean; redirectTo?: string },
): RouteDefinition => {
  const keepAlive =
    options?.keepAlive ??
    !TAB_KEEP_ALIVE_EXCLUDED_PATHS.has(path);

  if (options?.redirectTo) {
    return { path, element, redirectTo: options.redirectTo, keepAlive: false };
  }

  return { path, element, keepAlive };
};

export const buildProtectedRouteDefinitions = (
  c: ProtectedRouteComponents,
): RouteDefinition[] => [
  route('/profile', page(c.PerfilPage)),

  route('/sensoriamento/sensores', page(c.SensoresSensoriamentoPage)),
  route('/sensoriamento/relatorios', page(c.RelatoriosSensoriamentoPage)),
  route('/sensoriamento/ativacao', page(c.AtivacaoDispositivoPage)),

  route('/product-engineering', page(c.EngenhariaPage)),
  route('/product-engineering/eng-produto', page(c.EngProdutoPage)),
  route('/product-engineering/componentes', page(c.ComponentesPage)),
  route(
    '/product-engineering/processos',
    withPermission('sidebar_product_engineering_processo', c.ProcessosPage),
  ),
  route('/product-engineering/eng-produto/montagem', page(c.MontagemPage)),
  route('/product-engineering/eng-produto/montagem/:orcamentoId', page(c.MontagemPage)),

  route('/production/planning', page(c.ProductionPlanningPage)),
  route('/production/schedule', page(c.SequenciamentoPage)),
  route('/production/planning/ordens', page(c.OrdensProducaoPage)),
  route('/production/planning/processos', page(c.OperacoesPage)),
  route('/production/control', page(c.ProductionControlPage)),
  route('/production/control/auditoria', page(c.AuditoriaPage)),
  route('/production/control/nao-conformidades', page(c.NaoConformidadesPage)),
  route(
    '/production/control/lotes',
    withPermission('sidebar_production_control_lotes', c.LotesPage),
  ),
  route('/production/control/relatorios', page(c.RelatoriosPage)),
  route('/production/apontamento', page(c.ApontamentoPage)),
  route(
    '/production/control/monitoramento',
    withPermission('sidebar_production_monitoring', c.MonitoramentoPage),
  ),
  route(
    '/production/monitoring',
    withPermission('sidebar_production_monitoring', c.MonitoramentoPage),
  ),
  route('/production/dashboards', page(c.DashboardsGeraisPage)),
  route('/production/melhoria', page(c.MelhoriaPage)),
  route('/production/factory-view', page(c.FactoryViewPage), { keepAlive: false }),
  route('/production/resources', page(c.ParametrizacaoPage)),

  route('/maintenance/control/machine/:machineId', page(c.MachineDetailPage)),
  route('/maintenance/control/estoque', page(c.MaintenanceControlPlaceholderPage)),
  route('/maintenance/control/checklist', page(c.MaintenanceControlPlaceholderPage)),
  route('/maintenance/control/historico', page(c.MaintenanceControlPlaceholderPage)),
  route('/maintenance/control', page(c.MaintenanceControlPage)),
  route('/maintenance/planning', page(c.MaintenancePlanningPage)),
  route('/maintenance/dashboards', page(c.MaintenanceDashboardPage)),
  route('/maintenance/parametrizacao/sensores', page(c.MaintenanceSensoresPage)),

  route('/cadastros', page(c.CadastrosPage)),
  route('/cadastros/categoria', page(c.CadastrosPage)),
  route('/cadastros/produto', page(c.CadastrosPage)),
  route(
    '/cadastros/itens-consumo',
    withPermission('sidebar_cadastros_itens_consumo', c.ItensConsumoPage),
  ),
  route(
    '/cadastros/clientes',
    withPermission('sidebar_cadastros_clientes', c.ClientesPage),
  ),
  route(
    '/cadastros/formas-pagamento',
    withPermission('sidebar_cadastros_formas_pagamento', c.FormasPagamentoPage),
  ),
  route('/cadastros/parametrizacao', page(c.ParametrizacaoPage)),
  route('/cadastros/parametrizacao/setores', page(c.SetoresPage)),
  route('/cadastros/parametrizacao/maquinas', page(c.MaquinasPage)),
  route('/cadastros/parametrizacao/categorias', page(c.CategoriasPage)),
  route('/cadastros/parametrizacao/horarios', page(c.HorariosPage)),
  route('/cadastros/parametrizacao/causas', page(c.CausasPage)),

  route('/suprimentos', withPermission('sidebar_suprimentos', c.SuprimentosPage)),
  route(
    '/suprimentos/estoque',
    withPermission('sidebar_suprimentos_estoque', c.SuprimentosEstoquePage),
  ),
  route(
    '/suprimentos/separacoes',
    withPermission('sidebar_suprimentos_separacoes', c.SuprimentosSeparacoesPage),
  ),
  route(
    '/suprimentos/movimentacoes',
    withPermission('sidebar_suprimentos_movimentacoes', c.SuprimentosMovimentacoesPage),
  ),
  route('/suprimentos/movimentacoes/historico', null, { redirectTo: '/suprimentos/movimentacoes' }),
  route(
    '/suprimentos/parametrizacao',
    withPermission('sidebar_suprimentos_parametrizacao', c.SuprimentosParametrizacaoPage),
  ),
  route(
    '/suprimentos/notas-fiscais',
    withPermission('sidebar_suprimentos_notas_fiscais', c.SuprimentosNotasFiscaisPage),
  ),

  route('/sales', withPermissionElement('sidebar_sales', <Navigate to="/sales/pdv" replace />), {
    redirectTo: '/sales/pdv',
  }),
  route('/sales/pdv', withPermission('sidebar_sales_pdv', c.SalesPdvPage)),
  route('/sales/pedidos', withPermission('sidebar_sales_pedidos', c.SalesPedidosPage)),
  route('/sales/tabela', withPermission('sidebar_sales_tabela', c.SalesTabelaPage)),

  route('/quality', withPermissionElement('sidebar_quality', <Navigate to="/quality/parametros" replace />), {
    redirectTo: '/quality/parametros',
  }),
  route(
    '/quality/parametros',
    withPermission('sidebar_quality_parametros', c.ParametrosQualidadePage),
  ),
  route('/quality/checklist', withPermission('sidebar_quality_checklist', c.ChecklistPage)),
  route('/quality/regras', withPermission('sidebar_quality_regras', c.RegrasQualidadePage)),
  route(
    '/quality/auditoria',
    withPermission('sidebar_quality_auditoria', c.AuditoriaQualidadePage),
  ),
  route(
    '/quality/dashboards',
    withPermission('sidebar_quality_dashboards', c.DashboardsQualidadePage),
  ),
  route(
    '/quality/planejamento',
    withPermission('sidebar_quality_planejamento', c.PlanejamentoChecklistPage),
  ),
  route(
    '/quality/operacao',
    withPermission('sidebar_quality_operacao', c.OperacaoQualidadePage),
  ),

  route('/settings', page(c.SettingsPage)),
  route('/manual', page(c.ManualPage)),
];

export const buildAdminPanelRouteDefinitions = (
  c: ProtectedRouteComponents,
): RouteDefinition[] => [
  route(
    '/admin-panel',
    withPermission('sidebar_admin_panel', c.AdminPanel),
  ),
];

export const initializeProtectedRouteRegistry = (c: ProtectedRouteComponents): void => {
  setProtectedRouteDefinitions([
    ...buildProtectedRouteDefinitions(c),
    ...buildAdminPanelRouteDefinitions(c),
  ]);
};
